import type * as React from "react";
import { useCallback, useEffect } from "react";

import ProductImage, { type ProductImageWidths } from "~/domains/product/components/ProductImage";
import classNames from "classnames";
import Parameter from "~/common/components/Parameter";
import { type HousingProduct, HousingProductType } from "~/domains/product/types/ProductData";
import { Link, LinkButton, LinkType } from "~/common/components/Link";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { Card } from "~/features/configurator/Linear/components/Card";
import { useBoolMap } from "~/common/hooks/useBoolMap";
import { toMapFn } from "~/common/utils/reducers";
import { Collapse } from "~/common/mui";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { OldDesignationParameter } from "~/domains/product/components/ProductParams";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { INVALID_PACKAGE_CODE, MAX_QUANTITY } from "~/utils/constants";
import { userIsCustomer, userIsDistributor } from "~/domains/user/utils/roles";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { StockLevelSection } from "~/features/productDetails/components/AddToCartCard/StockLevelSection";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import { useFormContext } from "react-hook-form";
import { type AddToCartFormData, type OptionalPartFormData } from "~/features/addToCart/hooks/useAddToCartNew";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { Translation } from "~/app/i18n/Translation";
import { useSelector } from "react-redux";
import { getNetPrice, getSurcharge, getTariff } from "~/domains/product/utils/productDescriptorUtils";
import { selectProperAmountInCart } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { canSeeListPrice } from "~/utils/features";
import { useOnMount } from "~/common/hooks/useOnMount";
import { isSpecialPrice } from "~/features/productDetails/redux/productDetailsSelectors";
import { useTranslation } from "react-i18next";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { DEFAULT_PACK_CODE_PROVIDER } from "~/utils/cartHelper";
import { NetPrice } from "~/features/productDetails/components/ProductSummary/components/NetPrice";
import { ListPrice } from "~/features/productDetails/components/ProductSummary/components/ListPrice";
import { PackagingCellInput } from "~/features/cart/cart/components/PackagingCell";
import EditIcon from "@mui/icons-material/Edit";

type HeadlineLevels = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p";

interface Props {
  housingProducts: HousingProduct[];
  headlineLevel?: HeadlineLevels;
  className?: string;
}

export const OptionalPartsSection = ({ housingProducts, headlineLevel = "p", className }: Props) => {
  const Headline = headlineLevel;
  const { t } = useTranslation();

  const canExpand = userIsCustomer();

  const { map: expandedItemMap, toggle: toggleExpanded, replace } = useBoolMap();
  const isAllOpen =
    housingProducts.length === Object.keys(expandedItemMap).length &&
    housingProducts.every((item) => expandedItemMap[item.id]);

  const btnIcon = <Icon type={isAllOpen ? IconType.ChevronUp : IconType.ChevronDown} />;

  const toggleAll = useCallback(() => {
    replace(
      toMapFn(
        housingProducts,
        (item) => item.id,
        () => !isAllOpen,
      ),
    );
  }, [housingProducts, isAllOpen]);

  useOnMount(() => {
    if (userIsDistributor()) {
      replace(
        toMapFn(
          housingProducts,
          (product) => product.id,
          () => true,
        ),
      );
    }
  });

  return (
    <div className={classNames("scha__optional-parts-section", className)}>
      <Headline className="scha__optional-parts-section__title">
        {t("page.productDetails.housing.optional.title")}
      </Headline>
      <div className={"d-flex flex-justify-between mb-30"}>
        <p className="label-large m-0">{t("page.productDetails.housing.optional.subtitle")}</p>
        {canExpand && (
          <LinkButton onClick={toggleAll} iconBefore={btnIcon}>
            {t(`page.productDetails.housing.optional.${isAllOpen ? "closeall" : "openall"}`)}
          </LinkButton>
        )}
      </div>
      <div className="scha__optional-parts-section__items">
        {housingProducts.map((housingProduct, index) => (
          <OptionalPartsItem
            key={housingProduct.id}
            index={index}
            housingProduct={housingProduct}
            expanded={expandedItemMap[housingProduct.id]}
            onExpand={() => toggleExpanded(housingProduct.id)}
          />
        ))}
      </div>
    </div>
  );
};

interface ItemProps {
  housingProduct: HousingProduct;
  index: number;
  className?: string;
  expanded?: boolean;
  onExpand?: () => void;
}

const IMAGE_WIDTHS: ProductImageWidths = {
  product: "120px",
  manufacturer: "32px",
  brand: "27px",
  gap: "10px",
};

const OptionalPartsItem = ({ housingProduct, index, className, expanded, onExpand }: ItemProps) => {
  const { t } = useTranslation();
  const formProps = useFormContext<AddToCartFormData>();

  const priceReference = formProps.watch("priceReference");

  const optionalParts = formProps.watch("optionalParts");
  const { quantity, selected: active, selectedPackCode } = optionalParts[housingProduct.id];

  const updateData = (newData: Partial<OptionalPartFormData>) => {
    formProps.setValue("optionalParts", {
      ...formProps.getValues("optionalParts"),
      [housingProduct.id]: {
        ...optionalParts[housingProduct.id],
        ...newData,
      },
    });
  };

  const productCode = housingProduct.product.code;
  const descriptor = ProductService.useProductDescriptor({
    productCode,
    priceReference,
    options: { fetch: true },
  });

  const isSpecial = isSpecialPrice(descriptor, "");
  const priceProps = { productCode, priceReference, packCode: selectedPackCode };
  const cartQty = useSelector(selectProperAmountInCart(priceProps));
  const totalQuantity = quantity + cartQty;

  const updateQty = (newVal: number) => {
    const selectPriceProps = { ...priceProps, totalQuantity: newVal + cartQty, product: descriptor };
    const price = getNetPrice(selectPriceProps);
    const surcharge = getSurcharge(selectPriceProps);
    const tariffSurcharge = getTariff(selectPriceProps.product);
    updateData({ quantity: newVal, isQtyUserModified: true, netPrice: price, surcharge, tariffSurcharge });
  };
  const toggleSelected = () => updateData({ selected: !active });

  // Select the initial package if there is none
  useEffect(() => {
    if (selectedPackCode === INVALID_PACKAGE_CODE && descriptor.availability) {
      const packageProvider = DEFAULT_PACK_CODE_PROVIDER;
      if (packageProvider) {
        const selectedPackCode = packageProvider(descriptor, quantity) ?? INVALID_PACKAGE_CODE;
        const selectPriceProps = { ...priceProps, totalQuantity, product: descriptor, packCode: selectedPackCode };
        const price = getNetPrice(selectPriceProps);
        const surcharge = getSurcharge(selectPriceProps);
        const tariffSurcharge = getTariff(selectPriceProps.product);
        updateData({ selectedPackCode, netPrice: price, surcharge, tariffSurcharge });
      }
    }
  }, [descriptor.availability]);

  // Select the initial package if there is none
  useEffect(() => {
    const selectPriceProps = { ...priceProps, totalQuantity, product: descriptor, packCode: selectedPackCode };
    const price = getNetPrice(selectPriceProps);
    const surcharge = getSurcharge(selectPriceProps);
    const tariffSurcharge = getTariff(selectPriceProps.product);
    updateData({ netPrice: price, surcharge, tariffSurcharge });
  }, [descriptor.prices, cartQty]);

  const product = descriptor.base;
  const isHousing = housingProduct.type === HousingProductType.HOUSING;

  let designation = (
    <ProductDesignation
      size={"title-medium"}
      className={"scha__optional-parts-item__designation"}
      designation={product.designation}
      isPreferred={product.preferred}
      noCssColor={true}
    />
  );

  const canExpand = userIsCustomer();

  if (!isHousing && product.url) {
    designation = (
      <Link variant={LinkType.NAVIGATION} href={product.url} target={"_blank"}>
        {designation}
      </Link>
    );
  }

  const btnIcon = <Icon type={expanded ? IconType.ChevronUp : IconType.ChevronDown} />;

  const toggleExpandCb = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onExpand) {
      onExpand();
    }
  }, []);

  const selectedPackage = descriptor?.availability?.packagingInformation.find(
    (item) => item.packCode === selectedPackCode,
  );

  let primaryWarehouse = "-";
  if (selectedPackage) {
    primaryWarehouse = selectedPackage.totalWarehouse.name;
  }
  const packSize = selectedPackage?.packSize ?? 1;

  let label: string = "-";
  if (selectedPackage) {
    label = getPackagingLabelAtLineItem({
      t: t,
      productDescriptor: descriptor,
      quantity,
      packSize,
      packCode: selectedPackCode,
    });
  }

  const openEditModal = () => {
    AddToCartService.openEditPartWizard({
      productCode: descriptor.base.code,
      initialData: {
        quantity,
        selectedPackCode,
        priceReference,
        totalQuantity,
      },
      restrictionQuery: "",
      onSubmit: async (descriptor, data) => {
        updateData({
          ...data,
          isQtyUserModified: data.quantity !== quantity,
        });
        return true;
      },
    });
  };

  const listPriceVisible = canSeeListPrice({ product: descriptor, isSpecialPrice: isSpecial });

  return (
    <Card
      className={classNames("scha__optional-parts-item", className, isHousing && "scha__optional-parts-item--housing")}
      data-test-id={`pdp-housing-${housingProduct.id}`}
      active={active}
      onClick={toggleSelected}
    >
      <CheckedCircle className={"scha__optional-parts-item__checked"} checked={active} />
      <ProductImage className={"scha__optional-parts-item__image"} product={product} widths={IMAGE_WIDTHS} />

      {designation}

      <p className="scha__optional-parts-item__short-description">
        <Translation {...product.shortDescription} />
      </p>
      <div className="scha__optional-parts-item__description truncate-text">{product.description}</div>
      <Collapse in={expanded}>
        <div>
          <NewVersionMessage baseProduct={product} className={"mb-3"} />
          <OldDesignationParameter product={product} label={t("productDetailPage.oldDesignation")} className={"mb-3"} />
        </div>
      </Collapse>

      {descriptor.isPriceLoading && <CircleSpinner className={"mb-3"} />}
      {descriptor.prices && (
        <div className={"mb-3"}>
          {userHasPermission(Permission.PRICE) && (
            <NetPrice
              quantity={quantity}
              product={descriptor}
              packCode={selectedPackCode}
              sx={{
                marginBottom: "20px",
                '& [data-test-id="value"]': {
                  ...getFontStyle(FontSize.TitleLarge),
                  fontWeight: "bold",
                },
                '& [data-test-id="unit"]': {
                  ...getFontStyle(FontSize.LabelLarge),
                },
              }}
            />
          )}
          {listPriceVisible && <ListPrice product={descriptor} packCode={""} />}
        </div>
      )}
      {descriptor.isAvailabilityLoading && <CircleSpinner className={"mb-3"} />}
      {descriptor.availability && (
        <StockLevelSection
          className={"mb-2"}
          productDescriptor={descriptor}
          quantity={quantity}
          onCheckAvailabilityClick={openEditModal}
          checkAvailabilityDisabled={false}
        />
      )}

      <Collapse in={!expanded}>
        <Parameter className={"mb-3"} label={t("input.quantity")}>
          {quantity}
        </Parameter>
      </Collapse>

      <Collapse in={expanded}>
        <div className={"mb-3"}>
          <div onClick={NO_PROPAGATE}>
            <QuantityInput
              max={MAX_QUANTITY}
              min={packSize}
              value={quantity}
              onValueChange={updateQty}
              step={packSize}
            />
          </div>
          <div onClick={NO_PROPAGATE}>
            <PackagingCellInput
              disabled={descriptor.isLoading || !descriptor.availability}
              value={label}
              helperText={t(primaryWarehouse)}
              onClick={openEditModal}
              icon={<EditIcon />}
            />
          </div>
        </div>
      </Collapse>

      {canExpand && (
        <LinkButton iconBefore={btnIcon} onClick={toggleExpandCb} className="scha__optional-parts-item__see-more">
          {t(`page.pdp.housing.${expanded ? "closeDetails" : "openDetails"}`)}
        </LinkButton>
      )}
    </Card>
  );
};

const NO_PROPAGATE = (e: React.MouseEvent) => e.stopPropagation();
